import dayjs from "dayjs";

/**
 * Single source of truth for the reusable Schedule feature: all enums, option
 * builders and helpers live here so the schedule components stay free of inline
 * literals / magic strings.
 *
 * IMPORTANT: every value below is the **API vocabulary** — the exact strings the
 * `createReportSchedule` / `updateReportSchedule` mutations accept. Form field
 * names match the payload field names 1:1, so no adapter/translation layer is
 * needed between the form and the mutation.
 *
 * Labels are NOT stored here — they come from the `reportScheduling` i18n
 * namespace, keyed by these enum values (e.g. `frequencyOptions.DAILY`).
 */

/* -------------------------------------------------------------------------- */
/*                                 Frequencies                                */
/* -------------------------------------------------------------------------- */

export const FREQUENCY = {
  DAILY: "DAILY",
  WEEKLY: "WEEKLY",
  MONTHLY: "MONTHLY",
};

export const FREQUENCY_VALUES = Object.values(FREQUENCY);

/* -------------------------------------------------------------------------- */
/*                                   Weekdays                                  */
/* -------------------------------------------------------------------------- */

// API weekday names, ordered Monday-first to match the UI row.
export const WEEKDAY = {
  MONDAY: "MONDAY",
  TUESDAY: "TUESDAY",
  WEDNESDAY: "WEDNESDAY",
  THURSDAY: "THURSDAY",
  FRIDAY: "FRIDAY",
  SATURDAY: "SATURDAY",
  SUNDAY: "SUNDAY",
};

export const WEEKDAY_VALUES = Object.values(WEEKDAY);

/* -------------------------------------------------------------------------- */
/*                                Monthly modes                               */
/* -------------------------------------------------------------------------- */

export const MONTHLY_MODE = {
  DAY_OF_MONTH: "DAY_OF_MONTH", // a specific day number of the month
  NTH_WEEKDAY: "NTH_WEEKDAY", // e.g. "FIRST_MONDAY" / "LAST_FRIDAY"
};

// Ordinals the API's `monthlyWeekday` supports (`<ORDINAL>_<WEEKDAY>`).
export const MONTHLY_ORDINAL = {
  FIRST: "FIRST",
  SECOND: "SECOND",
  THIRD: "THIRD",
  FOURTH: "FOURTH",
  LAST: "LAST",
};

export const MONTHLY_ORDINAL_VALUES = Object.values(MONTHLY_ORDINAL);

// Special `monthlyWeekday` value sent alongside a DAY_OF_MONTH schedule when the
// user picks the "Last day" option — tells the backend to fire on the true last
// day of each month (not a fixed date).
export const MONTHLY_LAST_DAY = "LAST_DAY";

/** Build the API `monthlyWeekday` value, e.g. ("FIRST", "MONDAY") -> "FIRST_MONDAY". */
export const buildMonthlyWeekday = (ordinal, weekday) => `${ordinal}_${weekday}`;

/** Split an API `monthlyWeekday` value back into { ordinal, weekday }. */
export const parseMonthlyWeekday = value => {
  const [ordinal, weekday] = String(value || "").split("_");
  return MONTHLY_ORDINAL[ordinal] && WEEKDAY[weekday] ? { ordinal, weekday } : {};
};

/* -------------------------------------------------------------------------- */
/*                              Export / delivery                             */
/* -------------------------------------------------------------------------- */

export const EXPORT_FORMAT = {
  XLSX: "XLSX",
  PDF: "PDF",
  HTML: "HTML",
  CSV: "CSV",
};

export const EXPORT_FORMAT_VALUES = Object.values(EXPORT_FORMAT);

export const DELIVERY_MODE = {
  PER_REP: "per_rep", // one email per person
  COMBINED: "combined", // one email with all attachments
};

export const DELIVERY_MODE_VALUES = Object.values(DELIVERY_MODE);

// `deliveryMethod` is derived from the "Send an SMS" checkbox.
export const DELIVERY_METHOD = {
  EMAIL: "EMAIL",
  EMAIL_AND_SMS: "EMAIL_AND_SMS",
};

export const deliveryMethodFor = sendSms =>
  sendSms ? DELIVERY_METHOD.EMAIL_AND_SMS : DELIVERY_METHOD.EMAIL;

export const SCHEDULE_STATUS = {
  ACTIVE: "ACTIVE",
  PAUSED: "PAUSED",
  COMPLETED: "COMPLETED",
};

// Sortable columns → the `orderBy` value the schedules query expects. Used as
// the antd column `key`/`columnKey` too, so the table's sorter maps 1:1.
// (The Name column sorts by createdAt server-side.)
export const SCHEDULE_SORT_FIELD = {
  NAME: "name",
  NEXT_RUN: "nextRun",
};

/* -------------------------------------------------------------------------- */
/*                              Export menu keys                              */
/* -------------------------------------------------------------------------- */

export const EXPORT_MENU_KEYS = {
  SCHEDULE: "SCHEDULE",
  EMAIL: "EMAIL",
  DOWNLOAD: "DOWNLOAD",
};

/* -------------------------------------------------------------------------- */
/*                                  Defaults                                  */
/* -------------------------------------------------------------------------- */

export const DRAWER_WIDTH = 600;
export const TIME_FORMAT = "hh:mm A";
export const API_TIME_FORMAT = "HH:mm";
export const NEXT_RUN_PREVIEW_COUNT = 5;
export const DEFAULT_RUN_HOUR = 18;
export const MAX_DAY_OF_MONTH = 31;
// The day-of-month "Last" choice. The API takes an int, and both the UI preview
// and the backend clamp to the real length of each month, so 31 == last day.
export const LAST_DAY_OF_MONTH = MAX_DAY_OF_MONTH;

// How many recipient emails a ScheduleCard shows before collapsing to "+N".
export const VISIBLE_RECIPIENTS = 2;

/* -------------------------------------------------------------------------- */
/*                              Option builders                               */
/* -------------------------------------------------------------------------- */

/**
 * All option lists are built from the enums above + a `t` function bound to the
 * `reportScheduling` namespace, so labels stay translatable and values stay in
 * the API vocabulary.
 */

export const buildFrequencyOptions = t =>
  FREQUENCY_VALUES.map(value => ({ label: t(`frequencyOptions.${value}`), value }));

/** Short weekday labels (Mon/Tue/…) for the weekly row. */
export const buildWeekdayOptions = t =>
  WEEKDAY_VALUES.map(value => ({ label: t(`weekdays.${value}`), value }));

// Export-format options. Formats are acronyms (XLSX / PDF / HTML / CSV) — same
// in every locale, so the value is the label. The supported formats differ by
// report type, so the caller passes the allowed list (see reportRegistry's
// `exportFormats`); defaults to all when omitted.
export const buildExportFormatOptions = (formats = EXPORT_FORMAT_VALUES) =>
  formats.map(value => ({ label: value, value }));

// Each mode renders as a radio with a one-line explanation beneath it.
export const buildDeliveryModeOptions = t =>
  DELIVERY_MODE_VALUES.map(value => ({
    label: t(`deliveryModeOptions.${value}`),
    description: t(`deliveryModeHints.${value}`),
    value,
  }));

/** Every `<ORDINAL>_<WEEKDAY>` combination the API accepts, e.g. "First Monday". */
export const buildNthWeekdayOptions = t =>
  MONTHLY_ORDINAL_VALUES.flatMap(ordinal =>
    WEEKDAY_VALUES.map(weekday => ({
      label: `${t(`ordinals.${ordinal}`)} ${t(`weekdaysFull.${weekday}`)}`,
      value: buildMonthlyWeekday(ordinal, weekday),
    }))
  );

/**
 * Day-of-month options. Fixed 1..31 (not the current month's length) because a
 * schedule recurs across months of differing lengths; the backend clamps to each
 * month's real end. The "Last" entry maps to LAST_DAY_OF_MONTH.
 */
export const buildDayOfMonthOptions = t => [
  // The "Last day" option carries the string sentinel MONTHLY_LAST_DAY (not a
  // number) so it doesn't collide with the numeric "31" option — otherwise the
  // Select can't tell them apart and shows "31" when Last day is picked.
  { label: t("ordinals.LAST_WORKING_DAY"), value: MONTHLY_LAST_DAY },
  ...Array.from({ length: MAX_DAY_OF_MONTH }, (_, i) => ({ label: `${i + 1}`, value: i + 1 })),
];

/* -------------------------------------------------------------------------- */
/*                          Recipient input behaviour                         */
/* -------------------------------------------------------------------------- */

/**
 * Shared config for the free-form "Send to" / "CC" inputs.
 *
 * `mode="tags"` with `open={false}` gives a pure chips field: type an address,
 * press Enter (or comma / space) and it becomes a tag. There is no suggestion
 * dropdown at all, so the select arrow is suppressed too.
 */
export const EMAIL_TAGS_SELECT_PROPS = {
  mode: "tags",
  open: false,
  suffixIcon: null,
  tokenSeparators: [",", " "],
  className: "w-full",
};

/**
 * Default email copy for a schedule / immediate send, derived from the report
 * name. Shared by the Schedule and Email Now drawers so both open with the same
 * wording; users can edit or clear either field.
 */
export const buildDefaultEmailContent = (reportName, t) => ({
  emailSubject: t("defaults.emailSubject", { reportName }),
  emailBody: t("defaults.emailBody", { reportName }),
});

/**
 * Pull both recipient lists off an API schedule row. `recipients` is a list of
 * objects, `ccEmails` a list of plain strings — this normalises them to two
 * arrays of addresses so call sites don't repeat the shape handling.
 */
export const getScheduleRecipients = schedule => ({
  to: (schedule?.recipients || []).map(r => r?.email).filter(Boolean),
  cc: (schedule?.ccEmails || []).filter(Boolean),
});

/**
 * Decide which addresses to show inline and how many are left over.
 *
 * Primary recipients lead; a schedule with only CC addresses still shows them
 * rather than rendering as empty. The "+N" count covers *everything* hidden —
 * remaining primaries plus every CC — so the badge never under-reports.
 */
export const splitVisibleRecipients = (to = [], cc = []) => {
  const source = to.length ? to : cc;
  const visible = source.slice(0, VISIBLE_RECIPIENTS);
  return { visible, hidden: to.length + cc.length - visible.length };
};

/* -------------------------------------------------------------------------- */
/*                              Helper functions                              */
/* -------------------------------------------------------------------------- */

/** Basic email validator used by the "Send to" / "CC" multi-selects. */
export const isValidEmail = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());

/** First invalid address in a list, or undefined when all are valid. */
export const firstInvalidEmail = emails => (emails || []).find(e => !isValidEmail(e));

const formatTime = time => (time ? dayjs(time).format(TIME_FORMAT) : "");

const ordinalSuffix = n => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

/**
 * Presentational summary ({ frequencyLabel, detail }) for a saved schedule, used
 * by ScheduleCard. Takes the raw API-shaped schedule row plus a bound `t`, e.g.:
 *   Daily   -> "At 06:00 PM"
 *   Weekly  -> "On Monday, Tuesday At 06:00 PM"
 *   Monthly -> "On 1st At 10:00 AM" / "First Monday At 10:00 AM"
 */
export const buildScheduleSummary = (schedule, t) => {
  const row = schedule || {};
  const { frequency, runTime } = row;
  const time = formatTime(runTime ? dayjs(runTime, API_TIME_FORMAT) : null);
  const frequencyLabel = frequency ? t(`frequencyOptions.${frequency}`) : "";

  if (frequency === FREQUENCY.DAILY) {
    // List the weekdays too, unless it's every day (all 7 → just the time).
    const dailyDays = row.dayOfWeek || [];
    if (dailyDays.length && dailyDays.length < 7) {
      const days = dailyDays.map(d => t(`weekdaysFull.${d}`)).join(", ");
      return { frequencyLabel, detail: t("summary.onDaysAt", { days, time }) };
    }
    return { frequencyLabel, detail: t("summary.at", { time }) };
  }

  if (frequency === FREQUENCY.WEEKLY) {
    const days = (row.dayOfWeek || []).map(d => t(`weekdaysFull.${d}`)).join(", ");
    return { frequencyLabel, detail: t("summary.onDaysAt", { days, time }) };
  }

  if (frequency === FREQUENCY.MONTHLY) {
    if (row.monthlyMode === MONTHLY_MODE.NTH_WEEKDAY && row.monthlyWeekday) {
      const { ordinal, weekday } = parseMonthlyWeekday(row.monthlyWeekday);
      const day = ordinal ? `${t(`ordinals.${ordinal}`)} ${t(`weekdaysFull.${weekday}`)}` : "";
      return { frequencyLabel, detail: t("summary.onDayAt", { day, time }) };
    }
    const dom = row.dayOfMonth;
    // The backend stores the last day as 31 (its last-day value).
    const day = dom === LAST_DAY_OF_MONTH ? t("summary.lastDay") : ordinalSuffix(dom);
    return { frequencyLabel, detail: t("summary.onDayAt", { day, time }) };
  }

  return { frequencyLabel: "", detail: "" };
};
